import { ReactElement, useEffect, useMemo, useState } from 'react'
import { DrawControllerWrapper, SceneHud, SceneHudAction } from '@scene'
import { DoubleSide, Euler, Vector3 } from 'three'
import {
  Check,
  ControlCamera,
  Height,
  OpenWith,
  RotateRight,
  SquareFoot,
} from '@mui/icons-material'
import { useSelectionMode } from '@editor/hooks'
import { useControlStore } from '@editor/stores'
import {
  DraggableLine,
  ImmutableVector3,
  ScalingIndicator,
  TransientDrawState,
} from '@modugen/ui-toolkit'
import { Html } from '@react-three/drei'
import { useIfcStore } from '@stores'
import { useDisableIFCSelection } from 'src/state/hooks'
import { CONFIGURE_TRANSFORMATION_SELECTION_KEY } from '../../constants'
import useApplyIfcAlignment from '../../hooks/useApplyIfcAlignment'
import useIfcAlignmentPointsDrag from './useIfcAlignmentPointsDrag'
import {
  RIGHT_ANGLE_MIN_OFFSET_RAD,
  RIGHT_ANGLE_SNAP_THRESHOLD_RAD,
  getFrameAxisAngle,
  offsetToNearestRightAngle,
  rotateAroundZ,
} from './utils'

const IFCAlignmentScene = (): ReactElement => {
  const { selectionModeKey, unsetSelectionMode } = useSelectionMode()

  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapToAngles = useControlStore(state => state.snapToAngles)

  const isEnabled = selectionModeKey === CONFIGURE_TRANSFORMATION_SELECTION_KEY

  const firstPoint = useIfcStore(state => state.firstPoint)
  const secondPoint = useIfcStore(state => state.secondPoint)
  const thirdPoint = useIfcStore(state => state.thirdPoint)

  const setFirstPoint = useIfcStore(state => state.setFirstPoint)
  const setSecondPoint = useIfcStore(state => state.setSecondPoint)
  const setThirdPoint = useIfcStore(state => state.setThirdPoint)
  const resetIfcPoints = useIfcStore(state => state.resetPoints)

  const [previewSecondPoint, setPreviewSecondPoint] = useState<ImmutableVector3 | null>(null)
  const [previewThirdPoint, setPreviewThirdPoint] = useState<ImmutableVector3 | null>(null)

  // Sync effect: when the transformation selection mode is switched off before a full point triple
  // has been placed (no `thirdPoint`), discard the partial pick from the shared IFC store and clear
  // the local preview points. This must run as an effect rather than be derived during render
  // because it mutates external state (the Zustand store via `resetIfcPoints`) and local component
  // state — writing to either during render is disallowed; the reset is only valid as a side effect
  // of `isEnabled`/`thirdPoint` transitioning.
  useEffect(() => {
    if (!isEnabled && !thirdPoint) {
      resetIfcPoints()
      setPreviewSecondPoint(null)
      setPreviewThirdPoint(null)
    }
  }, [isEnabled, thirdPoint, resetIfcPoints])

  const constrainToFirstZ = (p: ImmutableVector3, first: ImmutableVector3): ImmutableVector3 => {
    return new ImmutableVector3(p.x, p.y, first.z)
  }

  const constrainThirdPoint = (
    p1: ImmutableVector3,
    p2: ImmutableVector3,
    candidate: ImmutableVector3,
  ): ImmutableVector3 => {
    const baseDir = p2.sub(p1)

    // fallback – if first and second are identical, just clamp z
    if (baseDir.length() === 0) {
      return constrainToFirstZ(candidate, p1)
    }

    // work in xy-plane; build perpendicular direction to the first segment
    const perp = new ImmutableVector3(-baseDir.y, baseDir.x, 0)
    const perpLen = perp.length()

    if (perpLen === 0) {
      return constrainToFirstZ(candidate, p1)
    }

    const perpNorm = new ImmutableVector3(perp.x / perpLen, perp.y / perpLen, 0)
    const delta = candidate.sub(p2)
    const projectedLength = delta.x * perpNorm.x + delta.y * perpNorm.y + delta.z * perpNorm.z

    return p2.addScaledVector(perpNorm, projectedLength)
  }

  const handleMouseMove = (state: TransientDrawState) => {
    const { drawPoint } = state

    if (!drawPoint || !firstPoint) return

    const currentPoint = new ImmutableVector3(drawPoint.x, drawPoint.y, drawPoint.z)

    if (!secondPoint) {
      const constrained = constrainToFirstZ(currentPoint, firstPoint)
      setPreviewSecondPoint(constrained)
      return
    }

    if (!thirdPoint) {
      const candidate = constrainToFirstZ(currentPoint, firstPoint)
      const constrained = constrainThirdPoint(firstPoint, secondPoint, candidate)
      setPreviewThirdPoint(constrained)
    }
  }

  const handleDrawEnd = (state: TransientDrawState) => {
    const { drawPoint } = state

    if (!drawPoint || !isEnabled) return

    const point = new ImmutableVector3(drawPoint.x, drawPoint.y, drawPoint.z)

    // first click -> fix first point and start plane/z-grid definition
    if (!firstPoint) {
      setFirstPoint(point)
      setPreviewSecondPoint(null)
      setPreviewThirdPoint(null)
      return
    }

    // second click -> constrained to same z plane as first point
    if (!secondPoint) {
      const constrained = constrainToFirstZ(point, firstPoint)
      setSecondPoint(constrained)
      setPreviewSecondPoint(null)
      setPreviewThirdPoint(null)
      return
    }

    // third click -> constrained to same z and orthogonal to first segment
    if (!thirdPoint) {
      const candidate = constrainToFirstZ(point, firstPoint)
      const constrained = constrainThirdPoint(firstPoint, secondPoint, candidate)
      setThirdPoint(constrained)
      setPreviewThirdPoint(null)
      unsetSelectionMode()
    }
  }

  const firstToSecondLine = useMemo(() => {
    if (!firstPoint) return null

    const end = secondPoint || previewSecondPoint
    if (!end) return null

    return {
      start: firstPoint,
      end,
    }
  }, [firstPoint, secondPoint, previewSecondPoint])

  const secondToThirdLine = useMemo(() => {
    if (!secondPoint) return null

    const end = thirdPoint || previewThirdPoint
    if (!end) return null

    return {
      start: secondPoint,
      end,
    }
  }, [secondPoint, thirdPoint, previewThirdPoint])

  const firstToSecondCenter = useMemo(() => {
    if (!firstToSecondLine) return null
    const { start, end } = firstToSecondLine
    return [(start.x + end.x) / 2, (start.y + end.y) / 2, (start.z + end.z) / 2] as [
      number,
      number,
      number,
    ]
  }, [firstToSecondLine])

  const secondToThirdCenter = useMemo(() => {
    if (!secondToThirdLine) return null
    const { start, end } = secondToThirdLine
    return [(start.x + end.x) / 2, (start.y + end.y) / 2, (start.z + end.z) / 2] as [
      number,
      number,
      number,
    ]
  }, [secondToThirdLine])

  // Which leg is x vs y by angle of second segment to first axis: 0–180° = left, 180–360° = right
  const segmentLabels = useMemo(() => {
    const defaultLabels = { first: 'x', second: 'y' } as const
    if (!firstPoint || !secondPoint) return defaultLabels
    const effectiveThird = thirdPoint ?? previewThirdPoint
    if (!effectiveThird) return defaultLabels
    const firstAxis = secondPoint.sub(firstPoint)
    const firstLen = Math.hypot(firstAxis.x, firstAxis.y)
    if (firstLen === 0) return defaultLabels
    const secondAxis = effectiveThird.sub(secondPoint)
    const secondLen = Math.hypot(secondAxis.x, secondAxis.y)
    if (secondLen === 0) return defaultLabels
    const ax = firstAxis.x / firstLen
    const ay = firstAxis.y / firstLen
    const bx = secondAxis.x / secondLen
    const by = secondAxis.y / secondLen
    const crossZ = ax * by - ay * bx
    const dot = ax * bx + ay * by
    let angleDeg = (Math.atan2(crossZ, dot) * 180) / Math.PI
    if (angleDeg < 0) angleDeg += 360
    const isLeft = angleDeg >= 0 && angleDeg < 180
    const firstSegmentIsX = isLeft
    return {
      first: firstSegmentIsX ? 'y' : 'x',
      second: firstSegmentIsX ? 'x' : 'y',
    }
  }, [firstPoint, secondPoint, thirdPoint, previewThirdPoint])

  const planeCenter = useMemo(() => {
    if (!firstPoint) return null

    // Center plane on the first point
    return firstPoint
  }, [firstPoint])

  const drawingAxis = useMemo(() => {
    if (!firstPoint || !secondPoint || thirdPoint) return undefined
    const baseDir = secondPoint.sub(firstPoint)
    if (baseDir.length() === 0) return undefined
    const perp = new ImmutableVector3(-baseDir.y, baseDir.x, 0)
    const perpLen = perp.length()
    if (perpLen === 0) return undefined
    return {
      origin: secondPoint,
      direction: new ImmutableVector3(perp.x / perpLen, perp.y / perpLen, 0),
    }
  }, [firstPoint, secondPoint, thirdPoint])

  const { startDrag, activeDragMode } = useIfcAlignmentPointsDrag()

  // While picking the transformation (three points) or dragging via the HUD (move/rotate), the IFC
  // GLB model must not be hoverable or selectable so clicks land on the draw plane instead.
  useDisableIFCSelection(isEnabled || activeDragMode !== null)

  const { applyAlignment } = useApplyIfcAlignment()

  // Whether the rotate drag magnetically snaps to 90° increments. Toggled from the HUD; on by
  // default.
  const [rotationSnapEnabled, setRotationSnapEnabled] = useState(true)

  // HUD anchored at the middle (second) point once the frame is fully picked: drag to translate
  // the whole frame in the xy-plane or vertically, rotate it around the middle point, or apply the
  // resulting transformation (last button, separated by a divider).
  const hasAllPoints = !!(firstPoint && secondPoint && thirdPoint)
  const showHud = hasAllPoints && !isEnabled

  // Offer a one-click "snap to the clean right angle" action only while the frame's orientation is
  // close to — but not already exactly at — a 90° multiple of the global X axis (e.g. 175° → 180°).
  const axisAngle = firstPoint && secondPoint ? getFrameAxisAngle(firstPoint, secondPoint) : 0
  const rightAngleOffset = Math.abs(offsetToNearestRightAngle(axisAngle))
  const isNearRightAngle =
    hasAllPoints &&
    rightAngleOffset >= RIGHT_ANGLE_MIN_OFFSET_RAD &&
    rightAngleOffset <= RIGHT_ANGLE_SNAP_THRESHOLD_RAD

  const snapToRightAngle = () => {
    if (!firstPoint || !secondPoint || !thirdPoint) return
    const delta = -offsetToNearestRightAngle(getFrameAxisAngle(firstPoint, secondPoint))
    setFirstPoint(rotateAroundZ(firstPoint, secondPoint, delta))
    setThirdPoint(rotateAroundZ(thirdPoint, secondPoint, delta))
  }

  const hudGroups: SceneHudAction[][] = [
    [
      {
        key: 'move-xy',
        tooltip: 'In der Ebene verschieben',
        icon: <OpenWith fontSize="small" />,
        onPointerDown: event => startDrag('xy', event),
        active: activeDragMode === 'xy',
        dataCy: 'ifc-align-move-xy',
      },
      {
        key: 'move-z',
        tooltip: 'Vertikal verschieben (z)',
        icon: <Height fontSize="small" />,
        onPointerDown: event => startDrag('z', event),
        active: activeDragMode === 'z',
        dataCy: 'ifc-align-move-vertical',
      },
    ],
    [
      {
        key: 'rotate',
        tooltip: 'Um den Mittelpunkt drehen',
        icon: <RotateRight fontSize="small" />,
        onPointerDown: event => startDrag('rotate', event, rotationSnapEnabled),
        active: activeDragMode === 'rotate',
        dataCy: 'ifc-align-rotate',
      },
      {
        key: 'rotate-snap',
        tooltip: rotationSnapEnabled ? 'Einrasten (90°) aktiv' : 'Einrasten (90°) aus',
        icon: <ControlCamera fontSize="small" />,
        onClick: () => setRotationSnapEnabled(enabled => !enabled),
        toggled: rotationSnapEnabled,
        dataCy: 'ifc-align-rotate-snap',
      },
      ...(isNearRightAngle
        ? [
            {
              key: 'rotate-right-angle',
              tooltip: 'Auf rechten Winkel ausrichten',
              icon: <SquareFoot fontSize="small" />,
              onClick: snapToRightAngle,
              dataCy: 'ifc-align-rotate-right-angle',
            },
          ]
        : []),
    ],
    [
      {
        key: 'apply',
        tooltip: 'Transformation anwenden',
        icon: <Check fontSize="small" />,
        onClick: applyAlignment,
        dataCy: 'ifc-align-apply',
      },
    ],
  ]

  const gridSize = 100
  const gridDivisions = 100
  const planeSize = 100

  return (
    <>
      <DrawControllerWrapper
        enabled={isEnabled}
        enableIndicator
        snapToCornersAndEdges={snapToCornersAndEdges}
        snapToAngles={snapToAngles}
        drawingAxis={drawingAxis}
        onMouseMove={handleMouseMove}
        onDrawEnd={handleDrawEnd}
      />

      {firstPoint && <ScalingIndicator position={firstPoint.v} color="#e53935" visible />}
      {secondPoint && <ScalingIndicator position={secondPoint.v} color="#43a047" visible />}
      {thirdPoint && <ScalingIndicator position={thirdPoint.v} color="#1e88e5" visible />}

      {showHud && secondPoint && (
        <SceneHud
          position={new Vector3(secondPoint.x, secondPoint.y, secondPoint.z)}
          groups={hudGroups}
        />
      )}

      {/* Visualize first-second segment */}
      {firstToSecondLine && (
        <>
          <DraggableLine
            line={firstToSecondLine}
            isVisible
            showIndicators={false}
            color="#4caf50"
            showLengthIndicator={false}
          />
          {firstToSecondCenter && (
            <Html position={firstToSecondCenter} center style={{ pointerEvents: 'none' }}>
              <span
                style={{
                  padding: '2px 6px',
                  background: '#4caf50',
                  color: '#fff',
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {segmentLabels.first}
              </span>
            </Html>
          )}
        </>
      )}

      {/* Visualize second-third segment */}
      {secondToThirdLine && (
        <>
          <DraggableLine
            line={secondToThirdLine}
            isVisible
            showIndicators={false}
            color="#2196f3"
            showLengthIndicator={false}
          />
          {secondToThirdCenter && (
            <Html position={secondToThirdCenter} center style={{ pointerEvents: 'none' }}>
              <span
                style={{
                  padding: '2px 6px',
                  background: '#2196f3',
                  color: '#fff',
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {segmentLabels.second}
              </span>
            </Html>
          )}
        </>
      )}

      {/* Grid and plane on the z-plane defined by the first point */}
      {planeCenter && (
        <>
          {/* Simple grid helper to visualize the working plane */}
          {/* Assumes standard three.js gridHelper; orientation may depend on global axes */}
          <gridHelper
            args={[gridSize, gridDivisions, '#bbbbbb', '#dddddd']}
            position={[planeCenter.x, planeCenter.y, planeCenter.z]}
            rotation={new Euler(Math.PI / 2)}
          />

          {/* Semi-transparent plane */}
          <mesh position={[planeCenter.x, planeCenter.y, planeCenter.z]}>
            <planeGeometry args={[planeSize, planeSize]} />

            <meshBasicMaterial
              color="#999999"
              transparent
              opacity={0.2}
              side={DoubleSide}
              depthWrite={false}
            />
          </mesh>
        </>
      )}
    </>
  )
}

export default IFCAlignmentScene
